import { SxProps, Theme } from "@mui/material";
import myPlanTokens, {
  myPlanResponsiveTokens,
} from "screens/Settings/MyPlan/MyPlan.tokens";
import smsCreditsTokens from "./SmsCredits.tokens";

const t = myPlanTokens;
const r = myPlanResponsiveTokens;
const s = smsCreditsTokens;

/** Card gutter, per breakpoint — matches the sibling Plan & Billing cards. */
const gutter = { xs: r.gutter, md: t.space.gutter };

/* ── Header ─────────────────────────────────────────────────────────────── */

/** ACTIVE / RUNNING LOW pill beside the title. */
export const badgeSx = (low: boolean): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  fontSize: s.badge.fontSize,
  fontWeight: s.badge.fontWeight,
  letterSpacing: s.badge.letterSpacing,
  color: low ? t.color.dangerText : t.color.successText,
  bgcolor: low ? t.color.dangerBg : t.color.successBg,
  px: s.badge.paddingX,
  py: s.badge.paddingY,
  borderRadius: t.radius.pill,
  lineHeight: "normal",
  whiteSpace: "nowrap",
});

/** Title + badge on one line, description under it. */
export const titleRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "9px",
  flexWrap: "wrap",
};

/**
 * The header's Buy control. It toggles the inline panel rather than opening a
 * dialog, so it has an open state: the filled brand button becomes a quiet
 * outlined "Close".
 */
export const buyToggleSx = (open: boolean): SxProps<Theme> => ({
  width: { xs: "100%", md: "auto" },
  height: { xs: r.buttonHeight, md: t.size.smallButtonHeight },
  minHeight: { xs: r.buttonHeight, md: t.size.smallButtonHeight },
  px: { xs: r.buttonPaddingX, md: "18px" },
  py: 0,
  borderRadius: t.radius.control,
  fontSize: { xs: r.buttonFontSize, md: t.font.button },
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  // The theme writes the `background` shorthand on the button slots, which
  // silently beats an `sx` `bgcolor` longhand — pin both states one
  // specificity step higher.
  "&.MuiButton-root": {
    background: open ? t.color.surface : t.color.ctaBg,
    color: open ? t.color.body : t.color.onAccent,
    border: open
      ? `${t.border.hairline} solid ${t.color.controlBorder}`
      : "none",
    "&:hover": {
      background: open ? t.color.hoverFill : t.color.ctaBgHover,
      borderColor: open ? t.color.ink : "transparent",
    },
    "&.Mui-disabled": {
      background: open ? t.color.surface : t.color.ctaBg,
      color: open ? t.color.body : t.color.onAccent,
      opacity: 0.5,
    },
  },
});

/* ── Stat strip ─────────────────────────────────────────────────────────── */

/**
 * Balance / used / last purchase. The plan card's own `statCellSx` lays four
 * cells out as a 2 x 2 grid below `md`, which leaves an odd cell with three —
 * these stack instead, so the rules stay a single column of hairlines.
 */
export const statStripSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: s.grid.statStrip },
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
};

export const statCellSx = (index: number, total: number): SxProps<Theme> => {
  const rule = `${t.border.hairline} solid ${t.color.rowDivider}`;
  return {
    px: gutter,
    py: { xs: "14px", md: t.space.statCellY },
    display: "flex",
    flexDirection: "column",
    gap: { xs: r.stat.gap, md: "5px" },
    minWidth: 0,
    borderRight: { xs: "none", md: index === total - 1 ? "none" : rule },
    borderBottom: { xs: index === total - 1 ? "none" : rule, md: "none" },
  };
};

/** Trailing " credits left" inside the balance figure. */
export const balanceUnitSx: SxProps<Theme> = {
  fontSize: s.balanceUnit.fontSize,
  fontWeight: s.balanceUnit.fontWeight,
  color: t.color.muted,
  letterSpacing: "normal",
};

/** Balance meter: how much of what was bought is still unspent. */
export const meterTrackSx: SxProps<Theme> = {
  height: s.meter.height,
  borderRadius: s.meter.radius,
  bgcolor: s.meter.track,
  mt: s.meter.marginTop,
  overflow: "hidden",
};

export const meterFillSx = (percent: number, low: boolean): SxProps<Theme> => ({
  width: `${percent}%`,
  height: s.meter.height,
  borderRadius: s.meter.radius,
  bgcolor: low ? s.meter.low : s.meter.healthy,
});

/* ── Low-balance strip ──────────────────────────────────────────────────── */

export const lowStripSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: s.lowStrip.gap,
  flexWrap: "wrap",
  px: gutter,
  py: "13px",
  bgcolor: s.lowStrip.bg,
  borderTop: `${t.border.hairline} solid ${s.lowStrip.border}`,
};

export const lowStripIconSx: SxProps<Theme> = {
  width: s.lowStrip.iconSize,
  height: s.lowStrip.iconSize,
  color: s.lowStrip.icon,
  flexShrink: 0,
};

export const lowStripTextSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: s.lowStrip.textFontSize,
  fontWeight: s.lowStrip.textFontWeight,
  color: s.lowStrip.text,
  lineHeight: 1.45,
};

export const lowStripLinkSx: SxProps<Theme> = {
  flexShrink: 0,
  p: 0,
  border: 0,
  background: "none",
  fontSize: s.lowStrip.linkFontSize,
  fontWeight: s.lowStrip.linkFontWeight,
  color: s.lowStrip.icon,
  cursor: "pointer",
  whiteSpace: "nowrap",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

/* ── Buy panel (inline, not an overlay) ─────────────────────────────────── */

export const buyPanelSx: SxProps<Theme> = {
  px: gutter,
  pt: "22px",
  pb: "24px",
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
  bgcolor: s.buyPanel.bg,
  display: "flex",
  flexDirection: "column",
  gap: s.buyPanel.gap,
};

export const buyPanelHeadingSx: SxProps<Theme> = {
  fontSize: s.buyPanel.headingFontSize,
  fontWeight: s.buyPanel.headingFontWeight,
  color: t.color.ink,
  lineHeight: "normal",
};

export const packGridSx: SxProps<Theme> = {
  display: "grid",
  // Four across on the design's desktop; two then one as the card narrows,
  // since a 19px figure and a price can't share a quarter of a phone.
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: s.grid.packs },
  gap: s.buyPanel.packGap,
};

/** One pack. Selected takes the brand border; its tag pill floats on the edge. */
export const packSx = (selected: boolean): SxProps<Theme> => ({
  position: "relative",
  bgcolor: t.color.surface,
  border: `${s.pack.borderWidth} solid ${
    selected ? t.color.ctaBg : t.color.cardBorder
  }`,
  borderRadius: s.pack.radius,
  p: s.pack.padding,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: s.pack.gap,
  boxSizing: "border-box",
  textAlign: "left",
  width: "100%",
  transition: "border-color .15s",
  "&:hover": { borderColor: t.color.ctaBg },
  "&:focus-visible": {
    outline: `2px solid ${t.color.ctaBg}`,
    outlineOffset: "2px",
  },
});

export const packTagSx: SxProps<Theme> = {
  position: "absolute",
  top: s.pack.tagTop,
  left: s.pack.tagLeft,
  fontSize: s.pack.tagFontSize,
  fontWeight: t.weight.heading,
  letterSpacing: s.pack.tagLetterSpacing,
  color: t.color.onAccent,
  bgcolor: t.color.ctaBg,
  p: s.pack.tagPadding,
  borderRadius: t.radius.pill,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

export const packCreditsSx: SxProps<Theme> = {
  fontSize: s.pack.creditsFontSize,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: "normal",
};

export const packLabelSx: SxProps<Theme> = {
  fontSize: s.pack.labelFontSize,
  fontWeight: t.weight.strong,
  letterSpacing: s.pack.labelLetterSpacing,
  color: t.color.muted,
  lineHeight: "normal",
};

export const packPriceSx: SxProps<Theme> = {
  fontSize: s.pack.priceFontSize,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  mt: s.pack.priceMarginTop,
  lineHeight: "normal",
};

export const packUnitSx: SxProps<Theme> = {
  fontSize: s.pack.unitFontSize,
  color: t.color.muted,
  lineHeight: "normal",
};

/* ── Custom amount ──────────────────────────────────────────────────────── */

export const customRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: s.custom.gap,
  flexWrap: "wrap",
};

export const customLabelSx: SxProps<Theme> = {
  fontSize: s.custom.labelFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

export const customFieldSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: s.custom.fieldGap,
  height: s.custom.fieldHeight,
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  borderRadius: s.custom.fieldRadius,
  bgcolor: t.color.surface,
  px: s.custom.fieldPaddingX,
  boxSizing: "border-box",
  "&:focus-within": { borderColor: t.color.ctaBg },
};

export const customInputSx: SxProps<Theme> = {
  width: s.custom.inputWidth,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: s.custom.inputFontSize,
  fontWeight: t.weight.medium,
  color: t.color.ink,
  fontFamily: "inherit",
  p: 0,
  // A number input's spinner would collide with the "credits" suffix.
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  MozAppearance: "textfield",
};

export const customSuffixSx: SxProps<Theme> = {
  fontSize: s.custom.suffixFontSize,
  fontWeight: t.weight.medium,
  color: t.color.muted,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

export const customHintSx: SxProps<Theme> = {
  fontSize: s.custom.hintFontSize,
  color: t.color.muted,
  lineHeight: "normal",
};

/* ── Order bar ──────────────────────────────────────────────────────────── */

export const orderBarSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: s.order.gap,
  flexWrap: "wrap",
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: s.order.radius,
  p: s.order.padding,
  boxSizing: "border-box",
};

export const orderLineSx: SxProps<Theme> = {
  fontSize: s.order.lineFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

/** The reassurance line — truncates rather than wrapping the bar to two rows. */
export const orderNoteSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: s.order.noteFontSize,
  color: t.color.muted,
  lineHeight: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const orderTotalSx: SxProps<Theme> = {
  ml: { md: "auto" },
  fontSize: s.order.totalFontSize,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

export const orderButtonSx = (enabled: boolean): SxProps<Theme> => ({
  height: s.order.buttonHeight,
  minHeight: s.order.buttonHeight,
  px: s.order.buttonPaddingX,
  py: 0,
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&.MuiButton-root": {
    background: enabled ? t.color.ctaBg : s.order.disabledBg,
    color: t.color.onAccent,
    border: "none",
    "&:hover": {
      background: enabled ? t.color.ctaBgHover : s.order.disabledBg,
    },
    "&.Mui-disabled": {
      background: s.order.disabledBg,
      color: t.color.onAccent,
    },
  },
});

/* ── Activity table ─────────────────────────────────────────────────────── */

export const activityHeaderSx: SxProps<Theme> = {
  // Below `md` the rows stop being a grid, so the strip has nothing to label.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: s.grid.activityRow,
  px: t.space.gutter,
  py: t.space.columnHeaderY,
  bgcolor: t.color.headerBg,
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
  borderBottom: `${t.border.hairline} solid ${t.color.headerBorder}`,
  fontSize: t.font.columnHeader,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.columnHeader,
  color: t.color.muted,
  lineHeight: "normal",
};

/** One activity line. Below `md` it stacks into a two-line block. */
export const activityRowSx = (isLast: boolean): SxProps<Theme> => ({
  display: { xs: "flex", md: "grid" },
  gridTemplateColumns: { md: s.grid.activityRow },
  alignItems: "center",
  gap: { xs: r.invoice.gap, md: 0 },
  px: gutter,
  py: { xs: "14px", md: t.space.rowY },
  borderBottom: isLast
    ? "none"
    : `${t.border.hairline} solid ${t.color.rowDivider}`,
});

export const activityLabelSx: SxProps<Theme> = {
  fontSize: t.font.rowText,
  fontWeight: t.weight.medium,
  color: t.color.ink,
  lineHeight: "normal",
};

export const activityDateSx: SxProps<Theme> = {
  fontSize: t.font.rowText,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: "normal",
};

export const activityDetailSx: SxProps<Theme> = {
  fontSize: t.font.sectionDesc,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: "normal",
};

/**
 * The credit change. Spend is grey rather than red: it is the wallet working
 * as intended, not a failure — only a purchase is called out, in green.
 */
export const activityDeltaSx = (isPositive: boolean): SxProps<Theme> => ({
  textAlign: { md: "right" },
  fontSize: t.font.rowText,
  fontWeight: t.weight.strong,
  color: isPositive ? t.color.successText : t.color.body,
  lineHeight: "normal",
  whiteSpace: "nowrap",
});

/** Text stack for the stacked (below `md`) form of a row. */
export const activityStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: r.invoice.textGap,
  minWidth: 0,
  flex: 1,
};

/* ── Footers ────────────────────────────────────────────────────────────── */

/**
 * Footer strip under the activity rows, holding the "Load more" control.
 * Mirrors the invoice list's pagination bar so the two cards close the same
 * way, but centred — this pages forward only, it has no page numbers.
 */
export const loadMoreBarSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  px: gutter,
  py: t.space.footerY,
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
};

export const loadMoreButtonSx: SxProps<Theme> = {
  height: t.size.smallButtonHeight,
  px: "18px",
  py: 0,
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  "&.MuiButton-root": {
    background: t.color.surface,
    color: t.color.body,
    border: `${t.border.hairline} solid ${t.color.controlBorder}`,
    "&:hover": {
      background: t.color.hoverFill,
      color: t.color.ink,
      borderColor: t.color.ink,
    },
  },
};

/** Validation / failure message inside the buy panel. */
export const errorTextSx: SxProps<Theme> = {
  fontSize: t.font.statCaption,
  fontWeight: t.weight.medium,
  color: t.color.dangerText,
  lineHeight: 1.45,
};

/** The "Manage payment methods" route offered beside a payment failure. */
export const paymentLinkSx: SxProps<Theme> = {
  fontSize: t.font.statCaption,
  fontWeight: t.weight.strong,
  color: t.color.accent,
  textDecoration: "underline",
  verticalAlign: "baseline",
  "&:hover": { color: t.color.accentHover },
};

/**
 * The better-value nudge under the custom amount. Amber rather than red: the
 * typed figure is valid and buyable, it is just worse value than a pack.
 */
export const cheaperPackSx: SxProps<Theme> = {
  px: "14px",
  py: "10px",
  borderRadius: t.radius.control,
  bgcolor: s.lowStrip.bg,
  border: `${t.border.hairline} solid ${s.lowStrip.border}`,
  fontSize: s.custom.hintFontSize,
  fontWeight: t.weight.medium,
  color: s.lowStrip.text,
  lineHeight: 1.5,
};

export const cheaperPackLinkSx: SxProps<Theme> = {
  p: 0,
  border: 0,
  background: "none",
  fontSize: s.custom.hintFontSize,
  fontWeight: t.weight.strong,
  color: s.lowStrip.icon,
  cursor: "pointer",
  textDecoration: "underline",
  whiteSpace: "nowrap",
};

/**
 * Shown in place of the stat strip when the balance read failed. Deliberately
 * not an `EmptyState`: that block says "nothing here yet", which would be a
 * lie about an account whose balance simply could not be read.
 */
export const loadFailedSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "12px",
  px: gutter,
  py: "18px",
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
};

export const loadFailedTextSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: t.font.sectionDesc,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: 1.5,
};

export const loadFailedRetrySx: SxProps<Theme> = {
  flexShrink: 0,
  p: 0,
  border: 0,
  background: "none",
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  color: t.color.accent,
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": { color: t.color.accentHover },
};
